class VestaMegaMenu extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    this.shell = this.querySelector('.vesta-mega-shell');
    if (!this.shell) return;
    this.header = this.closest('.header-wrapper');
    this.triggers = [...this.querySelectorAll('[data-menu-trigger]')];
    this.panels = [...this.querySelectorAll('[data-menu-panel]')];
    this.desktop = window.matchMedia('(min-width: 990px)');
    this.hover = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.events = new AbortController();
    const listen = (target, type, callback) => target?.addEventListener(type, callback, { signal: this.events.signal });

    this.panels.forEach((panel) => {
      panel.inert = true;
      panel.setAttribute('aria-hidden', 'true');
    });
    this.shell.inert = true;
    this.setAttribute('data-ready', '');

    this.triggers.forEach((trigger) => {
      const key = trigger.dataset.menuTrigger;
      listen(trigger, 'mouseenter', () => {
        if (this.hover.matches) this.open(key);
      });
      listen(trigger.querySelector('a'), 'click', (event) => {
        const touch = event.pointerType === 'touch' || (!this.hover.matches && event.detail > 0);
        if (touch && this.activeKey !== key && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
          event.preventDefault();
          this.open(key);
        }
      });
      listen(trigger.querySelector('button'), 'click', () => {
        if (this.activeKey === key) this.close();
        else this.open(key);
      });
      listen(trigger, 'keydown', (event) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        this.open(key);
        this.focusPanel();
      });
    });

    this.querySelectorAll('.vesta-nav__item:not([data-menu-trigger])').forEach((item) => {
      listen(item, 'mouseenter', () => this.scheduleClose());
    });
    listen(this, 'mouseenter', () => this.cancelClose());
    listen(this, 'mouseleave', () => this.scheduleClose());
    listen(this.shell, 'mouseenter', () => this.cancelClose());
    listen(this, 'focusin', () => this.cancelClose());
    listen(this, 'focusout', () => {
      queueMicrotask(() => {
        if (this.isConnected && !this.contains(document.activeElement) && !this.matches(':hover')) this.close();
      });
    });
    listen(document, 'keydown', (event) => {
      if (event.key !== 'Escape' || !this.activeKey) return;
      const restoreFocus = this.contains(document.activeElement);
      this.close(restoreFocus);
    });
    listen(document, 'pointerdown', (event) => {
      if (!this.contains(event.target)) this.close();
    });
    listen(this.desktop, 'change', () => this.close());
    listen(window, 'resize', () => this.updateHeight());
    listen(this.header, 'vesta:search-open', () => this.close());

    this.resizeObserver = new ResizeObserver(() => this.updateHeight());
    this.panels.forEach((panel) => this.resizeObserver.observe(panel.querySelector('.vesta-mega__inner'), { box: 'border-box' }));
  }

  disconnectedCallback() {
    this.close();
    this.events?.abort();
    this.events = null;
    this.resizeObserver?.disconnect();
    this.removeAttribute('data-ready');
  }

  open(key) {
    if (!this.desktop.matches) return;
    this.cancelClose();
    if (this.activeKey === key) return;
    const panel = this.panels.find((item) => item.dataset.menuPanel === key);
    if (!panel) return;
    const search = this.header?.querySelector('details-modal.header__search--apple');
    if (search?.querySelector('details[open]')) search.close(false);

    if (this.activePanel?.contains(document.activeElement)) {
      this.triggers.find((item) => item.dataset.menuTrigger === key).querySelector('button').focus({ preventScroll: true });
    }
    this.activeKey = key;
    this.activePanel = panel;
    this.panels.forEach((item) => {
      const active = item === panel;
      item.classList.toggle('is-active', active);
      item.inert = !active;
      item.setAttribute('aria-hidden', String(!active));
    });
    this.triggers.forEach((trigger) => {
      const active = trigger.dataset.menuTrigger === key;
      trigger.classList.toggle('is-active', active);
      trigger.querySelector('button').setAttribute('aria-expanded', String(active));
    });
    panel.scrollTop = 0;
    this.shell.inert = false;
    this.updateHeight();
    if (!this.hasAttribute('data-open')) this.shell.getBoundingClientRect();
    this.setAttribute('data-open', '');
    if (this.header) this.header.preventHide = true;
    document.body.classList.add('vesta-nav-open');
  }

  updateHeight() {
    if (!this.activePanel || !this.desktop.matches) return;
    const height = Math.ceil(this.activePanel.querySelector('.vesta-mega__inner').getBoundingClientRect().height);
    this.shell.style.setProperty('--vesta-panel-height', `${height}px`);
  }

  focusPanel() {
    if (!this.activePanel) return;
    const first = this.activePanel.querySelector('a[href]:not([tabindex="-1"]), button:not([disabled])');
    (first || this.activePanel).focus({ preventScroll: true });
  }

  cancelClose() {
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  scheduleClose() {
    this.cancelClose();
    this.closeTimer = setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 250);
  }

  close(restoreFocus = false) {
    this.cancelClose();
    if (!this.activeKey) return;
    const trigger = this.triggers.find((item) => item.dataset.menuTrigger === this.activeKey);
    if (restoreFocus) trigger?.querySelector('button').focus({ preventScroll: true });
    this.activeKey = null;
    this.activePanel = null;
    this.removeAttribute('data-open');
    this.shell.inert = true;
    this.panels.forEach((panel) => {
      panel.classList.remove('is-active');
      panel.inert = true;
      panel.setAttribute('aria-hidden', 'true');
    });
    this.triggers.forEach((item) => {
      item.classList.remove('is-active');
      item.querySelector('button').setAttribute('aria-expanded', 'false');
    });
    if (this.header) this.header.preventHide = false;
    document.body.classList.remove('vesta-nav-open');
  }
}

if (!customElements.get('vesta-mega-menu')) customElements.define('vesta-mega-menu', VestaMegaMenu);
